import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/app`;
const TOKEN_KEY = "brivio_app_token";

const AppAuthContext = createContext(null);
export const useAppAuth = () => useContext(AppAuthContext);

export const api = (token) =>
  axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

export const fileUrl = (url, token) => `${process.env.REACT_APP_BACKEND_URL}${url}?token=${token}`;

export function AppAuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Single shared notification poll — replaces separate polls in AppShell + BottomNav.
  const [notif, setNotif] = useState({ unreadNotifs: 0, unreadMessages: 0, latest: null });

  const refreshNotifs = useCallback(async () => {
    const tk = localStorage.getItem(TOKEN_KEY);
    if (!tk) { setNotif({ unreadNotifs: 0, unreadMessages: 0, latest: null }); return null; }
    try {
      const { data } = await api(tk).get("/notifications/summary");
      const next = {
        unreadNotifs: data.unread_notifications || 0,
        unreadMessages: data.unread_messages || 0,
        latest: data.latest || null,
      };
      setNotif(next);
      return next;
    } catch { return null; }
  }, []);

  const loadMe = useCallback(async (tk) => {
    try {
      const { data } = await api(tk).get("/auth/me");
      setUser(data.user);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) loadMe(token);
    else setLoading(false);
  }, [token, loadMe]);

  // One notification poll for the whole app (25s), paused when the tab is hidden.
  useEffect(() => {
    if (!token) { setNotif({ unreadNotifs: 0, unreadMessages: 0, latest: null }); return; }
    refreshNotifs();
    const t = setInterval(() => { if (document.visibilityState === "visible") refreshNotifs(); }, 25000);
    const onVis = () => { if (document.visibilityState === "visible") refreshNotifs(); };
    const onManual = () => refreshNotifs();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("brivio-notif-refresh", onManual);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("brivio-notif-refresh", onManual);
    };
  }, [token, refreshNotifs]);

  const persist = (tk, usr) => {
    localStorage.setItem(TOKEN_KEY, tk);
    setToken(tk);
    setUser(usr);
  };

  const signup = async (payload) => {
    const { data } = await api().post("/auth/signup", payload);
    persist(data.token, data.user);
    return data.user;
  };
  const login = async (email, password) => {
    const { data } = await api().post("/auth/login", { email, password });
    persist(data.token, data.user);
    return data.user;
  };
  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  };

  // Set a session directly from a token+user (used by phone/SMS auth).
  const setSession = (tk, usr) => persist(tk, usr);

  // Add provider capability to the current account (Customer -> Both) without a new account.
  const becomeProvider = async (payload = {}) => {
    const { data } = await api(localStorage.getItem(TOKEN_KEY)).post("/auth/become-provider", payload);
    setUser(data.user);
    return data.user;
  };

  return (
    <AppAuthContext.Provider value={{ token, user, setUser, loading, signup, login, logout, setSession, becomeProvider, notif, refreshNotifs }}>
      {children}
    </AppAuthContext.Provider>
  );
}
